import { useRef, useState, type CSSProperties } from "react";
import type { FeatureFlagKey } from "@klubora/shared";
import { deleteComingSoonImage, uploadComingSoonImage } from "../../lib/comingSoonImageUpload";
import { colors, fontSize, fontWeight, radius, space } from "../../lib/tokens";

interface Props {
  tenantId: string;
  featureKey: FeatureFlagKey;
  value: string;
  title: string;
  message: string;
  onChange: (next: string) => void;
}

/**
 * KLUB-161 — Option 3 (Bold). The phone preview IS the upload target, so the
 * operator sees the poster the member will see, not a thumbnail next to it.
 */
export function ComingSoonPosterField({
  tenantId,
  featureKey,
  value,
  title,
  message,
  onChange,
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState<"upload" | "delete" | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy("upload");
    setLocalError(null);
    try {
      const url = await uploadComingSoonImage(tenantId, featureKey, file);
      onChange(url);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const onClear = async () => {
    setBusy("delete");
    setLocalError(null);
    try {
      await deleteComingSoonImage(tenantId, featureKey);
      onChange("");
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ marginBottom: space.md }}>
      <span style={{
        display: "block", fontSize: fontSize.sm, fontWeight: fontWeight.medium,
        color: colors.stone, marginBottom: 6,
      }}>
        Imagem de fundo
      </span>
      <button
        type="button"
        onClick={onPick}
        disabled={busy !== null}
        aria-label={value ? "Trocar imagem de fundo" : "Enviar imagem de fundo"}
        style={{
          display: "block", width: 168, height: 336, padding: 0, margin: "0 auto",
          border: `1.5px dashed ${colors.fog}`, borderRadius: 22,
          background: colors.ink, cursor: busy ? "wait" : "pointer",
          overflow: "hidden", position: "relative",
        }}
      >
        {value ? (
          <img
            src={value}
            alt=""
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div style={{ position: "absolute", inset: 0, background: colors.ink }} />
        )}
        <div style={{
          position: "absolute", inset: 0,
          background: value
            ? "radial-gradient(ellipse at center, transparent 28%, rgba(0,0,0,.5) 80%), linear-gradient(180deg, rgba(0,0,0,.5) 0%, transparent 22%, transparent 50%, rgba(0,0,0,.7) 100%)"
            : "transparent",
        }} />
        <div style={{
          position: "relative", zIndex: 1, height: "100%",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          padding: "0 16px", textAlign: "center",
        }}>
          <div style={{
            width: 28, height: 2, background: colors.white, marginBottom: 10,
          }} />
          <div style={{
            fontSize: 13, fontWeight: fontWeight.semibold, color: colors.white,
            textShadow: "0 1px 3px rgba(0,0,0,.95), 0 0 14px rgba(0,0,0,.7)",
            marginBottom: 6, lineHeight: 1.2,
          }}>
            {title || "Em breve"}
          </div>
          <div style={{
            fontSize: 10, color: colors.white, lineHeight: 1.35,
            textShadow: "0 1px 3px rgba(0,0,0,.95), 0 0 14px rgba(0,0,0,.7)",
          }}>
            {busy === "upload"
              ? "Enviando..."
              : value
                ? (message || "")
                : "Clique para enviar a imagem"}
          </div>
        </div>
      </button>
      <div style={{
        display: "flex", justifyContent: "center", gap: 8, marginTop: 8,
      }}>
        <button
          type="button"
          onClick={onPick}
          disabled={busy !== null}
          style={ghostBtn}
        >
          {value ? "Trocar" : "Enviar"}
        </button>
        {value ? (
          <button
            type="button"
            onClick={onClear}
            disabled={busy !== null}
            style={{ ...ghostBtn, color: colors.error }}
          >
            {busy === "delete" ? "Removendo..." : "Remover"}
          </button>
        ) : null}
      </div>
      <span style={{
        display: "block", textAlign: "center", fontSize: fontSize.xs,
        color: colors.stoneSoft, marginTop: 6,
      }}>
        JPEG, PNG ou WebP · retrato 9:16
      </span>
      {localError ? (
        <span style={{
          display: "block", textAlign: "center", fontSize: fontSize.sm,
          color: colors.error, marginTop: 4,
        }}>
          {localError}
        </span>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={onFile}
        style={{ display: "none" }}
      />
    </div>
  );
}

const ghostBtn: CSSProperties = {
  height: 32, padding: "0 12px", background: colors.white, color: colors.ink,
  border: `1px solid ${colors.fog}`, borderRadius: radius.sm,
  fontSize: fontSize.sm, fontWeight: fontWeight.medium, cursor: "pointer",
};
